import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface CodeStep {
  name: string;
  nameEn: string;
  icon: string;
  description: string;
  tools: string[];
  color: string;
}

const CODE_STEPS: CodeStep[] = [
  {
    name: 'Capture 捕获',
    nameEn: 'Capture',
    icon: '📥',
    description: '将有价值的信息快速捕获到统一收集箱',
    tools: ['Readwise', 'Notion Web Clipper', '微信收藏'],
    color: '#4ECDC4',
  },
  {
    name: 'Organize 组织',
    nameEn: 'Organize',
    icon: '📁',
    description: '按 PARA 方法归类：项目 / 领域 / 资源 / 归档',
    tools: ['Notion', 'Obsidian', 'Logseq'],
    color: '#667EEA',
  },
  {
    name: 'Distill 提炼',
    nameEn: 'Distill',
    icon: '💎',
    description: '渐进式总结：高亮 → 加粗 → 核心摘要',
    tools: ['Zettelkasten', '渐进式总结法', '费曼笔记'],
    color: '#FFD700',
  },
  {
    name: 'Express 表达',
    nameEn: 'Express',
    icon: '🚀',
    description: '将知识转化为创造性输出：文章、演讲、项目',
    tools: ['博客写作', '知识分享', '项目实践'],
    color: '#FF6B35',
  },
];

const PARA = [
  { letter: 'P', name: 'Projects', desc: '当前有截止日期的项目', color: '#FF6B35' },
  { letter: 'A', name: 'Areas', desc: '长期负责的领域', color: '#4ECDC4' },
  { letter: 'R', name: 'Resources', desc: '未来可能有用的资源', color: '#667EEA' },
  { letter: 'A', name: 'Archives', desc: '已完成或不再活跃的', color: '#A4B0BE' },
];

export const SecondBrainAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentStep = Math.min(
    Math.floor(Math.max(0, frame - 60) / 105),
    CODE_STEPS.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 50% 30%, #111827 0%, #0B0E17 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #667EEA, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          第二大脑 CODE
        </h1>
        <p style={{ fontSize: 28, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Building a Second Brain · Tiago Forte 知识管理体系
        </p>
      </div>

      {/* CODE flow - horizontal pipeline */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          left: 55,
          right: 55,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {CODE_STEPS.map((step, i) => {
          const sProg = spring({
            frame: frame - 15 - i * 12,
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const isActive = i === currentStep;

          return (
            <React.Fragment key={step.nameEn}>
              <div
                style={{
                  flex: 1,
                  padding: '16px 20px',
                  background: isActive ? `${step.color}18` : `${step.color}08`,
                  border: `2px solid ${isActive ? step.color + '60' : step.color + '20'}`,
                  borderRadius: 16,
                  textAlign: 'center',
                  opacity: sProg,
                  transform: `scale(${sProg})`,
                  boxShadow: isActive ? `0 4px 24px ${step.color}25` : 'none',
                }}
              >
                <span style={{ fontSize: 36 }}>{step.icon}</span>
                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 700,
                    color: step.color,
                    marginTop: 4,
                  }}
                >
                  {step.name}
                </div>
              </div>
              {i < CODE_STEPS.length - 1 && (
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.2)', opacity: sProg }}>
                  →
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Detail panel */}
      {CODE_STEPS.map((step, i) => {
        const phaseStart = 60 + i * 105;
        const phaseEnd = phaseStart + 100;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 18], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 12, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === CODE_STEPS.length - 1 ? fadeIn : fadeIn * fadeOut;

        if (frame < phaseStart || opacity <= 0) return null;

        return (
          <div
            key={step.nameEn}
            style={{
              position: 'absolute',
              top: 500,
              left: 55,
              right: 55,
              opacity,
            }}
          >
            <div style={{ display: 'flex', gap: 40 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <span style={{ fontSize: 50 }}>{step.icon}</span>
                  <h2 style={{ fontSize: 42, fontWeight: 700, color: step.color, margin: 0 }}>
                    {step.name}
                  </h2>
                </div>

                <p style={{ fontSize: 28, color: '#E5E7EB', lineHeight: 1.7, margin: '0 0 24px' }}>
                  {step.description}
                </p>

                <div style={{ display: 'flex', gap: 12 }}>
                  {step.tools.map((tool, ti) => (
                    <div
                      key={tool}
                      style={{
                        padding: '10px 20px',
                        background: `${step.color}12`,
                        border: `1px solid ${step.color}30`,
                        borderRadius: 12,
                        fontSize: 24,
                        color: step.color,
                      }}
                    >
                      {tool}
                    </div>
                  ))}
                </div>
              </div>

              {/* PARA sidebar on Organize step */}
              {i === 1 && (
                <div style={{ width: 400 }}>
                  <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>
                    PARA 分类法
                  </div>
                  {PARA.map((p, pi) => {
                    const pProg = spring({
                      frame: frame - phaseStart - 20 - pi * 8,
                      fps,
                      config: { damping: 14, stiffness: 120 },
                    });
                    return (
                      <div
                        key={p.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          padding: '12px 16px',
                          marginBottom: 8,
                          background: `${p.color}08`,
                          border: `1px solid ${p.color}25`,
                          borderRadius: 12,
                          opacity: pProg,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 32,
                            fontWeight: 800,
                            color: p.color,
                            width: 36,
                            textAlign: 'center',
                          }}
                        >
                          {p.letter}
                        </span>
                        <div>
                          <div style={{ fontSize: 26, fontWeight: 600, color: p.color }}>
                            {p.name}
                          </div>
                          <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }}>
                            {p.desc}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        {CODE_STEPS.map((s, i) => (
          <div
            key={s.nameEn}
            style={{
              width: i === currentStep ? 44 : 12,
              height: 12,
              borderRadius: 6,
              background: i === currentStep ? s.color : 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
